import React, { useState, useRef, useEffect } from 'react';
import { X, MessageCircle, Send, Phone } from 'lucide-react';
import axios from 'axios';

const WHATSAPP_NUMBER = '919891550666';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      type: 'bot', 
      text: '🎄 Merry Christmas & Happy Holidays! 🎅\n\nWelcome to AI School for All! ❄️ Thank you for visiting us during this festive season!\n\nI\'m here to assist you with information about our programs and help you kickstart your career in Digital Marketing.\n\nTo provide you with personalized assistance, may I kindly ask for your name?' 
    }
  ]);
  const [currentInput, setCurrentInput] = useState('');
  const [userInfo, setUserInfo] = useState({
    name: '',
    city: '',
    email: '',
    phone: '',
    course: 'CSDGPM'
  });
  const [step, setStep] = useState(0);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const courseInfo = {
    curriculum: "🎓 The CSDGPM program covers 8 comprehensive modules over 6 months:\n\n1️⃣ Digital Marketing Fundamentals\n2️⃣ Search Engine Optimization (SEO)\n3️⃣ Search Engine Marketing (SEM)\n4️⃣ Social Media Marketing\n5️⃣ Performance Marketing\n6️⃣ AI-Powered Automation\n7️⃣ Analytics & Data\n8️⃣ Live Projects\n\n📚 150 Live Learning Hours with Industry Experts!",
    
    certification: "🏆 You'll earn:\n\n✅ Exemplar Global Certification - Internationally recognized by 100+ countries\n✅ Google Ads Certification preparation - 100% pass guarantee\n\nThese credentials will put your career at the top of every hiring list! 🚀",
    
    pricing: "🎄 CHRISTMAS SPECIAL OFFER! 🎁\n\n💰 Original Price: $599 USD\n🔥 Special Price: $299 USD (50% OFF!)\n\n⚡ Block your seat @ JUST $20!\n⏰ Offer valid till 31st December only!\n\nThis is our biggest discount of the year! 🎅",
    
    duration: "⏱️ Program Details:\n\n📅 Duration: 6 Months\n🎯 Training: 150 Live Hours\n📆 Status: 14th January 2025\n⏰ Last Date to Enroll: 7th January 2025\n\nFlexible timings with recorded sessions! 💻",
    
    schedule: "📅 Next Batch Details:\n\n🚀 Start Date: 14th January 2025\n⏰ Enrollment Deadline: 7th January 2025\n🎁 50% Discount Till: 31st December\n\nDon't miss this opportunity! Limited seats available! 🔥",
    
    career: "💼 Career Outcomes:\n\n📈 95% Placement Rate\n💰 Average Salary Hike: 150%\n🌍 500+ Hiring Partners\n👥 30,000+ Successful Alumni\n⭐ 4.9/5 Student Rating\n\nWe provide 100% Placement Assistance until you're hired! 🎯",

    workshops: "🎨 Short-Term Workshops (3-6 hours each):\n\n• Photo Editing with AI - Jan 3rd\n• Video Production with AI - Jan 3rd\n• Social Media Booster - Jan 10th\n• LinkedIn Automation - Jan 10th\n• Prompt Engineering - Jan 10th\n• Canva + AI Secrets - Jan 17th\n• AI for Freelancers - Jan 17th\n• Digital Ads Optimization - Jan 17th\n• Content Creation - Jan 24th\n• Resume Builder - Jan 24th\n\n🎁 All at 50% OFF this festive season!"
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim()) return;

    const userMessage = currentInput.trim();
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setCurrentInput('');

    if (step === 0) {
      setUserInfo(prev => ({ ...prev, name: userMessage }));
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: `🎄 It's wonderful to meet you, ${userMessage}! 🌟\n\nThank you for connecting with us during this festive season!\n\nCould you please share which city you're from? 🏙️` 
      }]);
      setStep(1);
    } else if (step === 1) {
      setUserInfo(prev => ({ ...prev, city: userMessage }));
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: `Wonderful! Great to connect with someone from ${userMessage}! 🌍\n\nMay I have your email address so we can send you exclusive festive offers and course information? 📧` 
      }]);
      setStep(2);
    } else if (step === 2) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userMessage)) {
        setMessages(prev => [...prev, { 
          type: 'bot', 
          text: 'Oops! That doesn\'t seem like a valid email. Could you please provide a valid email address? (e.g., yourname@example.com) 📧' 
        }]);
        return;
      }
      setUserInfo(prev => ({ ...prev, email: userMessage }));
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: `Perfect, thank you! ✅\n\nLastly, could you please share your phone number so our team can reach out to you with our Christmas special offers? 📱` 
      }]);
      setStep(3);
    } else if (step === 3) {
      const phoneRegex = /^\+?[\d\s\-()]{10,}$/;
      if (!phoneRegex.test(userMessage)) {
        setMessages(prev => [...prev, { 
          type: 'bot', 
          text: 'Please provide a valid phone number with at least 10 digits. 📱' 
        }]);
        return;
      }
      setUserInfo(prev => ({ ...prev, phone: userMessage }));
      
      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/course-leads`, {
          name: userInfo.name,
          city: userInfo.city,
          email: userInfo.email,
          phone: userMessage,
          course: 'CSDGPM - Chat Inquiry'
        });
      } catch (error) {
        console.error('Error submitting lead:', error);
      }

      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: `🎄 Excellent, ${userInfo.name}! 🎉\n\nYour details have been saved. Our team will contact you within 24 hours with exclusive Christmas offers!\n\n🎁 How may I assist you today? Ask me about:\n\n• 📚 Course curriculum & modules\n• 🏆 Certifications (Exemplar Global + Google)\n• 💰 Pricing & Christmas offers\n• 📅 Schedule & batch details\n• 💼 Career outcomes & placements\n• 🎨 Short-term workshops\n• 📞 Talk to our team\n\nWhat would you like to know? ✨` 
      }]);
      setStep(4);
    } else {
      const input = userMessage.toLowerCase();
      let response = '';

      if (input.includes('thank') || input.includes('thanks') || input === 'bye' || input === 'goodbye') {
        response = "🎄 You're most welcome! Merry Christmas! 🎅\n\nIt was a pleasure assisting you today. Our team will reach out within 24 hours.\n\n📞 For urgent queries:\n• WhatsApp: +91 98915 50566\n• Email: shrey@workshoplibrary.com\n\nWishing you a wonderful festive season! ❄️🎁";
      } else if (input.includes('human') || input.includes('agent') || input.includes('speak') || input.includes('talk') || input.includes('call')) {
        response = "👋 Absolutely! Our team would love to speak with you!\n\n📞 WhatsApp: +91 98915 50566\n📧 Email: shrey@workshoplibrary.com\n\nOr click the WhatsApp button below to chat directly! 💬\n\nWe'll get back to you within 24 hours. 🎄";
      } else if (input.includes('curriculum') || input.includes('module') || input.includes('syllabus') || input.includes('content')) {
        response = courseInfo.curriculum;
      } else if (input.includes('certif') || input.includes('credential') || input.includes('exemplar') || input.includes('google')) {
        response = courseInfo.certification;
      } else if (input.includes('price') || input.includes('cost') || input.includes('fee') || input.includes('offer') || input.includes('discount') || input.includes('$')) {
        response = courseInfo.pricing;
      } else if (input.includes('duration') || input.includes('how long') || input.includes('hour') || input.includes('month')) {
        response = courseInfo.duration;
      } else if (input.includes('schedule') || input.includes('start') || input.includes('date') || input.includes('batch') || input.includes('january')) {
        response = courseInfo.schedule;
      } else if (input.includes('career') || input.includes('job') || input.includes('salary') || input.includes('placement') || input.includes('hire')) {
        response = courseInfo.career;
      } else if (input.includes('workshop') || input.includes('short') || input.includes('quick')) {
        response = courseInfo.workshops;
      } else if (input.includes('enroll') || input.includes('apply') || input.includes('register') || input.includes('join')) {
        response = "🎄 Great choice! Here's how to enroll:\n\n1️⃣ Click 'Enroll Now' on any page\n2️⃣ Fill the enrollment form\n3️⃣ Pay just $20 to block your seat!\n\n🎁 Christmas Special: 50% OFF (till Dec 31st)\n💰 Full course: $299 instead of $599\n\nOur team will also contact you within 24 hours! 🎅";
      } else if (input.includes('whatsapp') || input.includes('chat')) {
        response = "📱 Chat with us on WhatsApp!\n\nClick the green WhatsApp button below or message us at:\n👉 +91 98915 50566\n\nWe're available to answer all your questions! 💬";
      } else {
        response = "🎄 Thank you for your question!\n\nI can help you with:\n\n• 📚 Course curriculum\n• 🏆 Certifications\n• 💰 Pricing & offers\n• 📅 Schedule & dates\n• 💼 Career outcomes\n• 🎨 Workshops\n• 📞 Contact our team\n\nWhat would you like to know? ✨";
      }

      setMessages(prev => [...prev, { type: 'bot', text: response }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent("Hi! I'm interested in the CSDGPM course. 🎄");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* WhatsApp Button */}
      <button
        onClick={openWhatsApp}
        className="bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all flex items-center gap-2"
        title="Chat on WhatsApp"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="font-semibold pr-1 hidden sm:inline">WhatsApp</span>
      </button>

      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #1a472a, #0d2818)' }}
        >
          <MessageCircle className="w-6 h-6" />
          <span className="font-semibold pr-2">🎄 Chat with Us</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl w-96 h-[550px] flex flex-col border-2 overflow-hidden" style={{ borderColor: '#1a472a' }}>
          {/* Header - Christmas Theme */}
          <div className="text-white p-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #1a472a, #0d2818)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl">
                🎄
              </div>
              <div>
                <h3 className="font-bold text-lg">AI School Assistant</h3>
                <p className="text-xs text-green-200">🎅 Christmas Special Support</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded-full p-1 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-green-50 to-red-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl p-3 shadow-sm ${
                    message.type === 'user'
                      ? 'text-white'
                      : 'bg-white text-gray-800 border border-green-200'
                  }`}
                  style={message.type === 'user' ? { background: 'linear-gradient(135deg, #1a472a, #0d2818)' } : {}}
                >
                  <p className="text-sm whitespace-pre-line leading-relaxed">{message.text}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* WhatsApp Quick Button */}
          <div className="px-4 py-2 bg-green-50 border-t border-green-200">
            <button
              onClick={openWhatsApp}
              className="w-full bg-green-500 hover:bg-green-600 text-white rounded-xl py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-all"
            >
              <Phone className="w-4 h-4" />
              Chat on WhatsApp: +91 98915 50566
            </button>
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <button
                onClick={handleSendMessage}
                disabled={!currentInput.trim()}
                className="text-white rounded-xl px-4 py-2 transition-all flex items-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #1a472a, #0d2818)' }}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
